import { LiveChartRefreshInterval } from "../constants/chartConstant";
import { CHART_DOWNLOAD_DATA_DIALOG_STRINGS } from "../constants/constantStrings";

let timerInterval = null;
const CHART_LINES_CONFIG = { showline: true, zeroline: false, linecolor: "#e1e1e1", linewidth: 1.5 };

const getChartTagTypes = Tags =>
  Tags.map(tag => {
    let types = {};
    if (tag.Type === "bar" || tag.Type === "Bar") {
      types = { ...tag, Type: "Bar" };
    } else if (tag.Type === "lines" || tag.Type === "Line") {
      types = { ...tag, Type: "lines" };
    } else if (tag.Type === "markers" || tag.Type === "Scatter") {
      types = { ...tag, Type: "markers" };
    } else if (tag.Type === "lines+markers" || tag.Type === "Scatter + Line") {
      types = { ...tag, Type: "lines+markers" };
    }
    return types;
  });

const ChartTypeConfig = {
  bar: {
    marker: { opacity: 0.5 }
  },
  lines: {
    line: { width: 1 }
  },
  "lines+markers": {
    marker: { size: 3 },
    line: { width: 1 }
  },
  markers: {
    marker: { size: 3 }
  }
};

const liveChartTimer = (isRunning, onTimeExpired) => {
  if (isRunning) {
    if (!timerInterval) {
      timerInterval = setInterval(() => {
        if (isRunning) onTimeExpired();
      }, LiveChartRefreshInterval);
    }
  } else {
    clearInterval(timerInterval);
    timerInterval = null;
  }
};

const getDefaultChartLayout = () => ({
  margin: { l: 60, r: 10, t: 20, b: 0, pad: 5 },
  hovermode: "closest",
  hoverlabel: { bordercolor: "#FFF" },
  legend: { y: -0.2, orientation: "h" }
});

const getTagConfig = (tag, isAnalytics) => {
  const configurations = JSON.parse(JSON.stringify(ChartTypeConfig)); // Deep Clone
  const config = configurations[tag.Type];
  if (isAnalytics) {
    config.mode = "markers"; // Set to "markers" to make it a scatter plot in analytics
  } else {
    config.mode = tag.Type;
  }
  config.type = "scattergl";
  config.hovertemplate = ` <b>%{y:,.2f} (${tag.Units}) %{x|%d %b %Y, %H:%M:%S} </b><br> %{meta}<extra></extra>`;
  config.name = `<span style="color:${tag.Color}; font-size: 12px; font-family: Diodrum">${tag.TagName}</span>`;
  if (config.line) {
    config.line.color = tag.Color;
  }
  if (config.marker) {
    config.marker.color = tag.Color;
  }
  return config;
};

const getMultiPlotChartYAxis = (text, color, nticks, fontSize) => ({
  automargin: true,
  tickfont: { color },
  nticks,
  title: {
    text,
    standoff: 5,
    font: { family: "Noto Sans", size: fontSize, color }
  },
  ...CHART_LINES_CONFIG
});

const getMultiPlotLayoutConfig = (tagsData, range, yTitleSize = 12, PlotZoomData, tagsforlimits) => {
  const tagsCount = tagsData.length;
  const chartLayout = {
    xaxis: { type: "date" },
    yaxis: { automargin: true, zeroline: false },
    margin: { l: 20, r: 20, b: 20, t: 35, pad: 2 },
    grid: {
      rows: tagsCount,
      columns: 1,
      pattern: "independent",
      roworder: "top to bottom"
    },
    showlegend: true
  };
  tagsData.forEach((tag, index) => {
    const xAxis = index === 0 ? "xaxis" : `xaxis${index + 1}`;
    const yAxis = index === 0 ? "yaxis" : `yaxis${index + 1}`;
    const showticklabels = tagsCount - 1 === index;
    chartLayout[xAxis] = { type: "date", showticklabels, automargin: true, showline: true, ...CHART_LINES_CONFIG, range };
    const nticks = tagsCount > 4 ? 3 : "auto";
    chartLayout[yAxis] = getMultiPlotChartYAxis(tag.Units, tag.Color, nticks, yTitleSize);
    if (tagsforlimits) {
      const lowerlimit = tagsforlimits[index].Ylower;
      const upperlimit = tagsforlimits[index].Yupper;
      const allDataPoints = tagsData[index].Data;
      const minDataValue = Math.min(...allDataPoints);
      const maxDataValue = Math.max(...allDataPoints);
      chartLayout[yAxis].range = getYAxisRange(lowerlimit, upperlimit, minDataValue, maxDataValue);
    }
    if (PlotZoomData?.zoomTagName === tag.TagName) {
      chartLayout[yAxis].range = PlotZoomData.yRange;
    }
  });
  return chartLayout;
};

const getExcelFileTitle = (worksheet, downloadDurationMsg) => {
  downloadDurationMsg = downloadDurationMsg?.replace("Download", "Downloaded");
  worksheet.duplicateRow(1, 2, true);
  worksheet.getRow(1).values = [downloadDurationMsg];
  worksheet.getRow(2).values = [];
  worksheet.mergeCells("A1:E2");
  worksheet.getCell("A1").alignment = { wrapText: true, horizontal: "center", vertical: "middle" };
  worksheet.getCell("F1").value = CHART_DOWNLOAD_DATA_DIALOG_STRINGS.excelNoteCell;
  worksheet.mergeCells("F1:J2");
  worksheet.getCell("F1").alignment = { wrapText: true, horizontal: "center", vertical: "middle" };
};

const getYAxisRange = (lowerlimit, upperlimit, minDataValue, maxDataValue) => {
  if (lowerlimit == null && upperlimit == null) {
    return [null, null]; // Return nulls for auto-ranging if no limits are provided.
  } else if (lowerlimit != null && upperlimit != null) {
    return [lowerlimit, upperlimit]; // Use provided limits.
  } else if (lowerlimit == null && upperlimit != null) {
    // Calculate lower limit if only upper limit is provided.
    const updatedLowerlimit = minDataValue === 0 ? -(5 * maxDataValue) / 100 : minDataValue < 0 ? minDataValue * 1.05 : minDataValue * 0.95;
    return [updatedLowerlimit, upperlimit]; // Set lower limit to 95% of minDataValue, or adjust based on max value if min is 0, to enhance visibility.
  } else if (lowerlimit != null && upperlimit == null) {
    // Calculate upper limit if only lower limit is provided.
    const updatedUpperlimit = maxDataValue === 0 ? (5 * minDataValue) / 100 : maxDataValue < 0 ? maxDataValue * 0.95 : maxDataValue * 1.05;
    return [lowerlimit, updatedUpperlimit]; // Set upper limit to 105% of maxDataValue, or adjust based on min value if max is 0, for clear visibility.
  }
};

const ChartUtil = {
  getChartTagTypes,
  liveChartTimer,
  getTagConfig,
  getDefaultChartLayout,
  getMultiPlotLayoutConfig,
  getExcelFileTitle,
  CHART_LINES_CONFIG,
  getYAxisRange
};

export default ChartUtil;
